// Copyright (c) Microsoft. All rights reserved.

// import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat_v2';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AgentModeChatInput } from './AgentModeChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { showAlertToast } from '../../redux/features/app/appSlice';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessage } from '../../libs/models/conversations/ChatMessage';
import { Button } from '../../components/Button';
import { Alerts } from '../../components/shared/Alerts';
import FileUploader from '../../components/FileUploader';
import { IFile } from '../../libs/models/conversations/File';
import classNames from 'classnames';
import { Icon } from '../../components/Icon';
import { ChatInsight } from './ChatInsight';
import { IApiResult } from '../../libs/models/app/APIResult';
import { Constants } from '../../Constants';
import { ExternalSource } from '../../libs/models/studio/PluginPromptList';

interface ChatRoomProps {
  scrollViewTargetRef: React.RefObject<HTMLDivElement>;
}

export const ChatRoom: React.FC<ChatRoomProps> = ({ scrollViewTargetRef }) => {
  const chat = useChat();
  const dispatch = useAppDispatch();

  const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations_v2);
  const { selectedPlugin } = useAppSelector((state: RootState) => state.plugins);

  const messages = React.useMemo(() => {
    return selectedId === ''
      ? { data: [] as ChatMessage[], continuationToken: null as string | null }
      : conversations[selectedId].messages;
  }, [conversations, selectedId]);
  const botStatus = React.useMemo(() => {
    return selectedId === '' ? false : conversations[selectedId].botResponseStatus;
  }, [conversations, selectedId]);

  const botStatusRef = React.useRef(botStatus);
  useEffect(() => {
    if (botStatus != botStatusRef.current) botStatusRef.current = botStatus;
  }, [botStatus]);

  const bottomRef = React.useRef<HTMLDivElement | null>(null);
  const [hasMore, setHasMore] = React.useState(false);

  const [isLoading, setIsLoading] = React.useState(true);
  const [isDragging, setIsDragging] = React.useState(false); // 드래그 여부
  const [uploadedFiles, setUploadedFiles] = React.useState<IFile[]>([]);
  const [isInsightLoading, setIsInsightLoading] = React.useState(true);

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const fileInput = document.querySelector('.file-uploader')?.parentElement;
    if (fileInput && fileInput.getAttribute('data-state') == 'show') return;
    if (!e.dataTransfer.types.includes('Files')) return;
    // setIsDraggingOver(true);
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    if (
      e.relatedTarget instanceof HTMLElement &&
      // Container에 포함된다면 return
      e.currentTarget.contains(e.relatedTarget)
    ) {
      return;
    }
    setIsDragging(false);
  };

  const [windowHeight, setWindowHeight] = useState(0);

  useEffect(() => {
    // 전체 메시지 높이 - window높이
    const handleResize = () => {
      const chatMain = document.querySelector('.chat-main');
      if (chatMain) {
        const scrollHeight = chatMain.scrollHeight;
        setWindowHeight(scrollHeight - window.innerHeight);
        chatMain.scrollTop = scrollHeight - window.innerHeight;
      }
    };
    // 초기 설정
    handleResize();

    // 윈도우 리사이즈 이벤트 리스너 추가
    window.addEventListener('resize', handleResize);

    // 컴포넌트 언마운트시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isDragging]);

  // 맨 아래로 이동
  const [showGoEndButton, setShowGoEndButton] = useState(false);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({
      behavior: botStatusRef.current ? 'auto' : 'smooth',
      block: 'start',
      // inline: 'center'
    });

  }, []);

  useEffect(() => {
    const chatMain = document.querySelector('.chat-main');
    if (chatMain) {
      const tolerance = 2; // 허용 오차 설정 (브라우저 zoom 변경 시 scroll 계산값이 정확히 0으로 떨어지지 않아서 showGoEndButton 설정 이슈 있음)
      const handleScroll = () => {
        //if(!botStatusRef.current){
        const isAtBottom = Math.abs(chatMain.scrollTop + chatMain.clientHeight - chatMain.scrollHeight) <= tolerance;
        if (isAtBottom && showGoEndButton) setShowGoEndButton(false);
        else if (!isAtBottom && !showGoEndButton) setShowGoEndButton(true);
      };
      //}

      chatMain.addEventListener('scroll', handleScroll);
      return () => {
        chatMain.removeEventListener('scroll', handleScroll);
      };
    }
    return;
  }, [showGoEndButton]);

  useEffect(() => {
    setHasMore(messages.continuationToken !== null);
    setIsLoading(false);
  }, [selectedId, messages.continuationToken]);


  useEffect(() => {
    if (messages.data.length !== 0 && !showGoEndButton) {
      scrollToBottom();
    }

  }, [messages, showGoEndButton]);

  useEffect(() => {
    if (messages.data.length !== 0) {
      scrollToBottom();
    }
  }, [selectedId]);

  const onLoadMore = () => {
    try {
      setIsLoading(true);
      if (hasMore) {
        setShowGoEndButton(false);
        chat.loadMessage(selectedId, false).catch(() => {
          setHasMore(false);
          const errorMessage = `메시지를 불러오는 중에 문제가 발생했어요. 상단 새로고침 버튼을 눌러주세요.`;
          dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));
        });
      }
    } catch { }
  };

  const handleSubmit = useCallback(
    (options: GetResponseOptions) => {
      console.log('handleSubmit', options)
      if (selectedId === '') {
        chat
          .createChat()
          .then((r) => {
            chat.getResponse({ ...options, chatId: r, sessionId: r });
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        chat.getResponse(options);
      }
      botStatusRef.current=true;
      scrollToBottom();
      setShowGoEndButton(false);
      setUploadedFiles([]);
    },
    [selectedId, chat.createChat, chat.getResponse,scrollToBottom],
  );


  const fileUpload = (files: File[]) => {
    setIsDragging(false);

    const sessionIdPromise = selectedId === '' ? chat.createChat() : Promise.resolve(selectedId);

    // 업로드된 파일 목록 추가
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    files.forEach(async (newFile) => {
      if (newFile.size > Constants.CHAT_MAX_SIZE) {
        dispatch(
          showAlertToast({
            altMessage: ERROR_MESSAGES["Request body too Large"],
            altType: AlertType.Info,
          }),
        );
        return;
      }

      if (!uploadedFiles.some((existingFile) => existingFile.origin.name === newFile.name)) {
        const file: IFile = {
          origin: newFile,
          result: false,
        };
        setUploadedFiles((prevFiles) => [...prevFiles, file]);
        await updateFileInput(file, await sessionIdPromise);
        if (file.result) setUploadedFiles((prevFiles) => [...prevFiles]);
        else
          setTimeout(() => {
            fileDelete(file);
          }, 1000);
      }
    });
  };

  const ERROR_MESSAGES: Record<string, string> = {
    DRM: '문서DRM이 적용된 문서의 경우 DRM해제 후 업로드해주세요.',
    NotSupported: '지원되지 않는 확장자입니다.',
    'Request body too Large': `${Constants.CHAT_MAX_SIZE / (1024 * 1024)}mb 이하의 파일만 업로드 가능합니다.`,
    AbcLabFileUploadNotAllowed: 'ABC Lab에서 파일 업로드 사용을 허용으로 변경 후 이미지 파일을 첨부해주세요.',
    WrongParameter: '업로드된 AIP 문서에 개인정보 패턴이 발견되어 업로드할 수 없습니다.',
    Default: '파일 업로드가 실패하였습니다. 사내 보안 정책에 따라 파일 업로드가 차단될 수 있습니다.',
  };

  const updateFileInput = async (file: IFile, sessionId: string) => {
    const agentId = selectedPlugin?.externalSource === ExternalSource.Aionu ? selectedPlugin.id : undefined;
    await chat
      .uploadFile(file.origin, agentId, sessionId)
      .then((result) => {
        file.result = true;
        file.analyzeResult = result;
      })
      .catch((error: unknown) => {
        console.log(error);
        const apiError = error as IApiResult;

        const message = apiError.status in ERROR_MESSAGES ? ERROR_MESSAGES[apiError.status] : ERROR_MESSAGES.Default;
        dispatch(
          showAlertToast({
            altMessage: message,
            altType: AlertType.Info,
          }),
        );
        file.result = false;
      });
  };

  const fileDelete = (fileToDelete: IFile) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  // smooth scroll
  // const easeInOutQuad = (t: number) => {
  //   return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  // };

  return (
    <div className="chat-main-height" onDragOver={onDragEnter} onDragLeave={onDragLeave}>
      {/* <div onDragOver={onDragEnter} onDragLeave={onDragLeave}> */}
      <div className={classNames('chat-room ', { 'is-hidden': isDragging }, { 'ing-skeleton': isInsightLoading })}>
        <Alerts />
        {selectedId === '' || messages.data.length === 0 ? (
          <ChatInsight
            onSubmit={handleSubmit}
            isInsightLoading={isInsightLoading}
            setIsInsightLoading={setIsInsightLoading}
          />
        ) : (
          <ChatHistory
            messages={messages.data}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            isLoading={isLoading}
            botStatus={botStatus}
            scrollViewTargetRef={scrollViewTargetRef}
          />
        )}
        <div ref={bottomRef} ></div>
        <div className="chat-bottom">
          <div className={classNames('go-end', { blind: messages.data.length === 0 })}>
            <Button
              control
              textBlind
              onClick={() => {
                setShowGoEndButton(false);
                scrollToBottom();
              }}
              data-state={!showGoEndButton ? 'hidden' : null}
            >
              끝으로
            </Button>
          </div>
          {/* -답변중일 때는 입력창에 보이는 textarea, button 요소에 disabled 속성 추가 */}{' '}
            <AgentModeChatInput
              uploadedFiles={uploadedFiles}
              fileUpload={fileUpload}
              fileDelete={fileDelete}
              onSubmit={handleSubmit}
              scrollToBottom={scrollToBottom} // 추가
            />
          <p className="chat-warning">
            <Icon name="info" />
            Works AI의 답변에 잘못된 정보가 있을 수 있습니다. 중요한 정보는 확인해 주세요.
          </p>
          {/* <ChatCaution text="잔여 질문 수" /> */}
        </div>
      </div>
      <div className="chat-attach">
        <FileUploader
          onFileUpload={fileUpload}
          className={isDragging ? 'is-show' : ''}
          style={isDragging ? { top: `calc(${windowHeight}px)` } : {}}
        />
      </div>
    </div>
  );
};
